import { Button, Skeleton, SxProps, useTheme, Theme, Box } from "@mui/material";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import { TooltipPlacementType } from "@ui/atoms/BaseTypography/BaseTypography";
import {
  getButtonStyles,
  ButtonColor,
  ButtonState,
  ButtonTreatment,
  getButtonDisabledStyles,
  getSoftButtonStyles
} from "@themes/utils/button";

export interface Props {
  text: string | React.ReactNode;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  dataTestId?: string;
  id?: string;
  disabled?: boolean;
  /**
   * Secondary disable flag (e.g. mid-submission). Kept separate from `disabled`
   * for backward compatibility; combined with `loading`/`disabled` to gate the
   * button.
   */
  status?: boolean;
  tooltipText?: string | React.ReactNode;
  tooltipPlacement?: TooltipPlacementType;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children?: React.ReactNode;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  /**
   * `default` and `control` are identical (`theme.controls.height`); `control`
   * is kept for existing callers. `compact` is the table-cell size.
   */
  size?: "compact" | "control" | "default";
  /**
   * `soft`: a tone-tinted fill — its pastel for the ground, its ring for the
   * edge, the tone (darkened where it must be) for the label — for a control
   * on a toned row. Renders as an MUI `text` button with a 1px border; the
   * theme's border-box height absorbs it, so it matches a contained sibling.
   */
  variant?: "contained" | "outlined" | "text" | "soft";
  color?: ButtonColor;
  type?: "button" | "submit" | "reset";
}

export const BaseButton: React.FC<Props> = ({
  children,
  color = "primary",
  dataTestId,
  disabled,
  endIcon,
  fullWidth,
  id,
  loading = false,
  onClick,
  size = "default",
  startIcon,
  status,
  sx,
  text,
  tooltipPlacement = "top",
  tooltipText,
  type = "button",
  variant = "contained"
}) => {
  const theme = useTheme();
  const containedButtonColor =
    color === "secondary"
      ? undefined
      : color === "primary"
        ? theme.vars.ui.button.primary
        : theme.vars.palette[color].main;

  const isCompact = size === "compact";
  // Undefined lets `getButtonStyles` fall back to the control radius.
  const sizeBorderRadius = isCompact
    ? Number(theme.shape.borderRadius)
    : undefined;

  const containedStyles = (state: ButtonState) =>
    getButtonStyles(
      theme,
      containedButtonColor,
      state,
      undefined,
      false,
      sizeBorderRadius,
      color === "primary" ? ButtonTreatment.PRIMARY_CONTAINED : undefined
    );

  const hasNoFill = !containedButtonColor;

  // Only `compact` contributes metrics; `default`/`control` take the theme's.
  // The radius is set here as well as on `getButtonStyles` because the two
  // reach different elements: this covers the root for every variant/colour,
  // while `getButtonStyles` covers the glass button's pseudo-elements.
  const sizeStyles = isCompact && {
    borderRadius: `${theme.shape.borderRadius}px`,
    minHeight: theme.controls.compact.height,
    padding: `0 ${theme.controls.compact.paddingX}px`
  };

  // Caller `sx` may be an object, a theme callback, or an array — normalise to
  // array entries so callbacks survive (an object spread would drop them).
  const callerSx = Array.isArray(sx) ? sx : [sx];

  const buttonStyleSetup: SxProps<Theme> = [
    {
      // A button label is a single phrase — breaking "Add Comment" across two
      // lines to fit a cramped row reads as broken, not responsive. Let the
      // button keep its natural width and make the LAYOUT give way instead.
      whiteSpace: "nowrap"
    },
    sizeStyles,
    ...callerSx,
    // After the caller's `sx`, so a variant's colouring wins over caller
    // overrides, as before; the two variant branches are mutually exclusive.
    variant === "contained" && {
      ...containedStyles(ButtonState.DEFAULT),
      "&:active": {
        ...containedStyles(ButtonState.ACTIVE),
        ...(hasNoFill && {
          backgroundColor: theme.vars.palette.border.subtle
        })
      },
      "&:disabled": { ...getButtonDisabledStyles(theme) },
      "&:hover": {
        ...containedStyles(ButtonState.HOVER),
        ...(hasNoFill && {
          backgroundColor: theme.vars.palette.surface.inset
        })
      }
    },
    variant === "soft" && {
      ...getSoftButtonStyles(theme, color),
      // Outranks the soft styles' own dark override on specificity, so one
      // rule covers both schemes.
      "&:disabled": { ...getButtonDisabledStyles(theme) }
    }
  ];

  const skeletonSx: SxProps<Theme> = [sizeStyles, ...callerSx];

  const buttonContent = (
    <Button
      disableRipple
      data-test-id={dataTestId}
      disabled={loading || status || disabled}
      onClick={onClick}
      // MUI has no soft variant; it is a text button under our own fill.
      variant={variant === "soft" ? "text" : variant}
      data-variant={variant}
      color={color}
      startIcon={startIcon}
      endIcon={endIcon}
      id={id}
      type={type}
      sx={loading ? skeletonSx : buttonStyleSetup}
      fullWidth={fullWidth}
    >
      <span>{text}</span>
      {children}
    </Button>
  );

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={fullWidth ? "100%" : undefined}
        sx={{
          borderRadius: `${sizeBorderRadius ?? theme.shape.borderRadiusButton}px`
        }}
      >
        {buttonContent}
      </Skeleton>
    );
  }

  if (disabled && tooltipText) {
    return (
      <BaseTooltip placement={tooltipPlacement} title={tooltipText}>
        <Box>{buttonContent}</Box>
      </BaseTooltip>
    );
  }

  return buttonContent;
};
